import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';

const ClienteForm = ({ onClose, onSuccess, initialData, empresas = [], isSuperAdmin = false, currentUserEmpresaId = null }) => {
    const toast = useToast();
    const isEditing = Boolean(initialData?.id_cliente);
    
    const [formData, setFormData] = useState({
        nombres: '',
        apellidos: '',
        codigo_cliente: '',
        direccion_cliente: '',
        telefono_cliente: '',
        email_cliente: '',
        sector: '',
        fecha_registro: new Date().toISOString().split('T')[0],
        id_empresa: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (initialData) {
            setFormData({
                nombres: initialData.nombres || '',
                apellidos: initialData.apellidos || '',
                codigo_cliente: initialData.codigo_cliente || '',
                direccion_cliente: initialData.direccion_cliente || '',
                telefono_cliente: initialData.telefono_cliente || '',
                email_cliente: initialData.email_cliente || '',
                sector: initialData.sector || '',
                fecha_registro: initialData.fecha_registro || new Date().toISOString().split('T')[0],
                id_empresa: initialData.id_empresa || currentUserEmpresaId || ''
            });
        } else {
            // For new cliente, set empresa from user context
            setFormData(prev => ({
                ...prev,
                id_empresa: isSuperAdmin ? '' : (currentUserEmpresaId || '')
            }));
        }
    }, [initialData, isSuperAdmin, currentUserEmpresaId]);

    // Auto-select first empresa for SuperAdmin if empty AND not editing
    useEffect(() => {
        if (!initialData && isSuperAdmin && !formData.id_empresa && empresas.length > 0) {
            setFormData(prev => ({ ...prev, id_empresa: empresas[0].id || empresas[0].id_empresa }));
        }
    }, [empresas, isSuperAdmin, formData.id_empresa, initialData]);

    const validateCedula = (cedula) => {
        if (!cedula) return 'La cédula es obligatoria';
        if (!/^\d{10}$/.test(cedula)) return 'La cédula debe tener exactamente 10 dígitos numéricos';
        return null;
    };

    const validateEmail = (email) => {
        if (!email) return null; // Optional
        if (!/\S+@\S+\.\S+/.test(email)) return 'Formato de email inválido';
        return null;
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.nombres?.trim()) {
            newErrors.nombres = 'Los nombres son obligatorios';
        }
        if (!formData.apellidos?.trim()) {
            newErrors.apellidos = 'Los apellidos son obligatorios';
        }
        
        const cedulaError = validateCedula(formData.codigo_cliente);
        if (cedulaError) {
            newErrors.codigo_cliente = cedulaError;
        }
        
        const emailError = validateEmail(formData.email_cliente);
        if (emailError) {
            newErrors.email_cliente = emailError;
        }

        // Only require empresa selection for SuperAdmin
        if (isSuperAdmin && !formData.id_empresa) {
            newErrors.id_empresa = 'Seleccione una empresa';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        
        // Clear error on change
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Por favor corrija los errores del formulario');
            return;
        }

        setIsLoading(true);

        try {
            // For non-SuperAdmin, ensure empresa ID is set from context
            const dataToSend = { ...formData };
            if (!isSuperAdmin && !dataToSend.id_empresa) {
                dataToSend.id_empresa = currentUserEmpresaId;
            }

            if (isEditing) {
                await AdminService.updateCliente(initialData.id_cliente, dataToSend);
                toast.success('Cliente actualizado exitosamente');
            } else {
                await AdminService.createCliente(dataToSend);
                toast.success('Cliente creado exitosamente');
            }
            onSuccess();
        } catch (error) {
            console.error('Error saving cliente:', error);
            const errorMessage = typeof error === 'string' ? error : (error.response?.data?.error || 'Error al guardar el cliente');
            
            if (errorMessage.includes('ya existe')) {
                setErrors(prev => ({ ...prev, codigo_cliente: errorMessage }));
                // Toast removed to avoid redundancy
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
            size="lg"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} isLoading={isLoading}>
                        <Save className="w-4 h-4 mr-2" />
                        {isEditing ? 'Actualizar' : 'Crear'}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                {/* Empresa (SuperAdmin only) */}
                {isSuperAdmin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Empresa <span className="text-red-500">*</span>
                        </label>
                        <select 
                            value={formData.id_empresa}
                            onChange={(e) => handleChange('id_empresa', e.target.value)}
                            className={`w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                errors.id_empresa ? 'border-red-500' : 'border-gray-300'
                            }`}
                        >
                            <option value="">Seleccione una empresa</option>
                            {empresas.map((empresa) => (
                                <option key={empresa.id || empresa.id_empresa} value={empresa.id || empresa.id_empresa}>
                                    {empresa.nombre}
                                </option>
                            ))}
                        </select>
                        {/* Error handled by select styling or add specific error component if needed, but select is native HTML here */}
                        {errors.id_empresa && (
                            <p className="text-red-500 text-xs mt-1">{errors.id_empresa}</p>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Cédula */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Cédula <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={formData.codigo_cliente}
                            onChange={(e) => handleChange('codigo_cliente', e.target.value)}
                            placeholder="1234567890"
                            maxLength={10}
                            error={errors.codigo_cliente}
                        />
                    </div>

                    {/* Nombres */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Nombres <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={formData.nombres}
                            onChange={(e) => handleChange('nombres', e.target.value)}
                            placeholder="Juan"
                            error={errors.nombres}
                        />
                    </div>

                    {/* Apellidos */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Apellidos <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="text"
                            value={formData.apellidos}
                            onChange={(e) => handleChange('apellidos', e.target.value)}
                            placeholder="Pérez"
                            error={errors.apellidos}
                        />
                    </div>
                </div>

                {/* Dirección */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Dirección
                    </label>
                    <Input
                        type="text"
                        value={formData.direccion_cliente}
                        onChange={(e) => handleChange('direccion_cliente', e.target.value)}
                        placeholder="Calle Principal, Barrio..."
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Teléfono */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Teléfono
                        </label>
                        <Input
                            type="tel"
                            value={formData.telefono_cliente}
                            onChange={(e) => handleChange('telefono_cliente', e.target.value)}
                            placeholder="0999123456"
                        />
                    </div>

                    {/* Sector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Sector
                        </label>
                        <Input
                            type="text"
                            value={formData.sector}
                            onChange={(e) => handleChange('sector', e.target.value)}
                            placeholder="Barrio Norte"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Email
                        </label>
                        <Input
                            type="email"
                            value={formData.email_cliente}
                            onChange={(e) => handleChange('email_cliente', e.target.value)}
                            placeholder="cliente@email.com"
                            error={errors.email_cliente}
                        />
                    </div>

                    {/* Fecha de Registro */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Fecha de Registro
                        </label>
                        <Input
                            type="date"
                            value={formData.fecha_registro}
                            onChange={(e) => handleChange('fecha_registro', e.target.value)}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ClienteForm;
